import React, { useEffect, useMemo, useState } from 'react';

import classNames from 'classnames';
import { RadarHeader, Table } from 'pages/Radar/components';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { link } from 'settings/navigation/link';
import {
  requestGetCampaignByIdEffect,
  requestGetCampaignMembersByCampaignIdEffect,
  requestUpdateCampaignEffect,
} from 'store/effects/radarKits';
import { selectedSequenceSelector, selectedSequenceStateSelector } from 'store/selectors/radarKits';
import { AssignClientsModal } from '../AssignClientsModal';
import { PlusIcon } from '../components';
import parentStyles from '../styles.module.scss';
import styles from './styles.module.scss';
import { Wrapper } from 'components';
import { PENDING } from 'settings/constants/apiState';
import { useAssignedClient } from './hooks/use-assigned-client.hook';
import { useCampaignEvents } from './hooks/use-campaign-events.hook';

export const AssignedClients: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const dispatch = useDispatch();

  const selectedSequence = useSelector(selectedSequenceSelector);
  const state = useSelector(selectedSequenceStateSelector);
  const isPending = state === PENDING;
  const [activeTab, setActiveTab] = useState<number>(0);

  useEffect(() => {
    dispatch(requestGetCampaignByIdEffect(id));
  }, []);

  const {
    upcomingColumns,
    upcomingDataSource,
    pastColumns,
    pastDataSource,
    failedColumns,
    failedDataSource,
    overdueColumns,
    overdueDataSource,
    canceledColumns,
    canceledDataSource,
    loading,
    refreshMemberData,
  } = useCampaignEvents();

  const {
    enrolledColumns,
    selectedMembers,
    campaignMembers,
    setCampaignMembers,
    setSelectedMembers,
    enrolledClientsLoading,
    enrolledClientsDataSource,
    isAssignClientsModalOpen,
    setIsAssignClientsModalOpen,
    unsubscribedAddresses,
  } = useAssignedClient({ onMemberUpdate: refreshMemberData });

  const navigateToAssignedClientView = (id: any, clientId: string) => {
    history.push(link.toRadarKitSequenceAssignedClientView(id, clientId));
  };

  const Placeholder = ({ type }) => (
    <div className={styles.placeholder}>There isn’t any {type} activity for this sequence. </div>
  );

  const renderTab = () => {
    switch (activeTab) {
      case 0:
        return (
          <div>
            <Table
              disableHeader
              data={enrolledClientsDataSource}
              columns={enrolledColumns}
              handleRowClick={(record: any) => {
                navigateToAssignedClientView(id, record.user.id);
              }}
              rowKey={(_, i) => i}
            />
          </div>
        );
      case 1:
        return (
          <div>
            {failedDataSource.length ? (
              <Table
                disableHeader
                count={failedDataSource.length}
                data={failedDataSource}
                columns={failedColumns}
                rowKey={(_, i) => i}
                className={styles.table}
              />
            ) : (
              <Placeholder type={'failed'} />
            )}
          </div>
        );
      case 2:
        return (
          <div>
            {overdueDataSource.length ? (
              <Table
                disableHeader
                count={overdueDataSource.length}
                data={overdueDataSource}
                columns={overdueColumns}
                rowKey={(_, i) => i}
                className={styles.table}
              />
            ) : (
              <Placeholder type={'overdue'} />
            )}
          </div>
        );
      case 3:
        return (
          <div>
            {upcomingDataSource.length ? (
              <Table
                disableHeader
                count={upcomingDataSource.length}
                data={upcomingDataSource}
                columns={upcomingColumns}
                rowKey={(_, i) => i}
                className={styles.table}
              />
            ) : (
              <Placeholder type={'upcoming'} />
            )}
          </div>
        );
      case 4:
        return (
          <div>
            {pastDataSource.length ? (
              <Table
                disableHeader
                count={pastDataSource.length}
                data={pastDataSource}
                columns={pastColumns}
                rowKey={(_, i) => i}
                className={styles.table}
              />
            ) : (
              <Placeholder type={'past'} />
            )}
          </div>
        );

      case 5:
        return (
          <div>
            {canceledDataSource.length ? (
              <Table
                disableHeader
                count={canceledDataSource.length}
                data={canceledDataSource}
                columns={canceledColumns}
                rowKey={(_, i) => i}
                className={styles.table}
              />
            ) : (
              <Placeholder type={'canceled'} />
            )}
          </div>
        );

      default:
        return null;
    }
  };

  const tabs = [
    ...(enrolledClientsDataSource?.length
      ? [
          {
            label: 'Enrolled',
            count: enrolledClientsDataSource?.length,
            id: 0,
          },
        ]
      : []),
    ...(failedDataSource?.length
      ? [
          {
            label: 'Failed',
            count: failedDataSource?.length,
            id: 1,
          },
        ]
      : []),
    ...(overdueDataSource?.length
      ? [
          {
            label: 'Overdue',
            count: overdueDataSource?.length,
            id: 2,
          },
        ]
      : []),
    {
      label: 'Upcoming',
      count: upcomingDataSource?.length,
      id: 3,
    },
    {
      label: 'Past',
      count: pastDataSource?.length,
      id: 4,
    },
    ...(canceledDataSource?.length
      ? [
          {
            label: 'Canceled',
            count: canceledDataSource?.length,
            id: 5,
          },
        ]
      : []),
  ];

  const isTabLoading = useMemo(() => {
    switch (activeTab) {
      case 0:
        return enrolledClientsLoading;
      case 1:
        return loading.failed;
      case 2:
        return loading.overdue;
      case 3:
        return loading.upcoming;
      case 4:
        return loading.past;
      case 5:
        return loading.canceled;
      default:
        return false;
    }
  }, [activeTab, enrolledClientsLoading, loading]);

  return (
    <>
      <div className={parentStyles.pageWrapper}>
        <RadarHeader
          rightChildren={
            <div className={styles.btnWrapper}>
              <button
                className={styles.btn}
                onClick={(e) => {
                  e.stopPropagation();
                  setIsAssignClientsModalOpen(true);
                }}
              >
                <div>
                  <PlusIcon />
                </div>
                Enroll
              </button>
            </div>
          }
        >
          <div className={parentStyles.titleInputContainer}>
            <div className={parentStyles.title}>Clients</div>
            <div className={parentStyles.description}>{selectedSequence?.Name}</div>
          </div>
        </RadarHeader>
        <div className={styles.tabWrapper}>
          {tabs.map(({ label, count, id }) => (
            <div
              className={classNames(styles.tabItem, { [styles.active]: activeTab === id })}
              onClick={() => setActiveTab(id)}
            >
              {label} <span>({count})</span>
            </div>
          ))}
        </div>
        <Wrapper isPending={isTabLoading} className={parentStyles.pageContent}>
          <div className={parentStyles.tableContainer}>{renderTab()}</div>
        </Wrapper>
      </div>
      {isAssignClientsModalOpen && (
        <AssignClientsModal
          isOpen={isAssignClientsModalOpen}
          handleClose={() => {
            dispatch(
              requestUpdateCampaignEffect(
                {
                  Id: Number(id),
                  CampaignMembers: selectedMembers,
                },
                {},
                (err, res) => {
                  if (!err && res.data.result.Id) {
                    dispatch(
                      requestGetCampaignMembersByCampaignIdEffect(
                        id,
                        { silent: true },
                        (err, res) => {
                          if (!err) {
                            const members = res?.data?.result || [];
                            setCampaignMembers(members);
                            setSelectedMembers(
                              members.flatMap((client) =>
                                client.member.Addresses.map((address) => ({
                                  ClientId: client.member.ClientId,
                                  AddressId: address.AddressId,
                                })),
                              ),
                            );
                            refreshMemberData();
                          }
                        },
                      ),
                    );
                  }
                  setIsAssignClientsModalOpen(false);
                },
              ),
            );
          }}
          closeDialog={() => {
            setIsAssignClientsModalOpen(false);
          }}
          subHeading="Assign clients to this kit"
          selected={selectedMembers}
          unsubscribedAddresses={unsubscribedAddresses}
          setSelected={setSelectedMembers}
        />
      )}
    </>
  );
};
